// Man hinh chinh: chon buoc -> nhap tham so -> chay -> xem log truc tiep.

import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import Console from "../components/Console";
import ParamForm, { macDinh, type ParamValues } from "../components/ParamForm";
import FullRunPanel, {
  coNguyHiem,
  khoiTao as khoiTaoFull,
  thieuThamSo,
  type FullRunState,
} from "../components/FullRunPanel";
import { Alert, Badge, ErrorBox, Spinner, Switch } from "../components/ui";
import { useRunStream } from "../hooks/useRunStream";
import type { Health, Step } from "../types";

function CoBuoc({ step }: { step: Step }) {
  return (
    <div className="step-flags">
      {step.needs_chrome ? <Badge tone="info" title="Cần Chrome + phiên đăng nhập">Chrome</Badge> : null}
      {step.needs_llm_key ? <Badge tone="accent" title="Cần API key Gemini/Groq">API key</Badge> : null}
      {step.dangerous ? <Badge tone="danger" title="Có thể ghi thật lên hệ thống">Ghi thật</Badge> : null}
      {!step.script_exists ? <Badge tone="danger">Thiếu script</Badge> : null}
    </div>
  );
}

/** Canh bao truoc nhung dieu kien buoc nay can ma he thong dang thieu. */
function ThieuDieuKien({ step, health }: { step: Step; health: Health | null }) {
  if (!health) return null;
  const thieu: string[] = [];
  if (step.needs_chrome && !health.chrome.ok) thieu.push("chưa tìm thấy Chrome trên máy chạy backend");
  // Phien panel la cua RIENG tung nguoi, doc tu health.toi chu khong phai
  // file .session.json dung chung (file do khong con dung nua).
  if (step.needs_chrome && health.toi && !health.toi.co_phien_panel)
    thieu.push(
      "tài khoản của bạn chưa có phiên đăng nhập panel — vào mục Tài khoản, " +
        "bấm “Đăng nhập lại panel” và tick captcha một lần",
    );
  if (step.needs_llm_key && !health.api_key.ok)
    thieu.push("chưa thêm API key nào ở mục API key");

  if (!thieu.length) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <Alert tone="warn">
        <strong>Bước này có thể hỏng vì:</strong>
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {thieu.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </Alert>
    </div>
  );
}

export default function PipelinePage({ health, napLaiHealth }: {
  health: Health | null;
  napLaiHealth: () => void;
}) {
  const [steps, setSteps] = useState<Step[]>([]);
  const [confirmPhrase, setConfirmPhrase] = useState("GHI THAT");
  // null = dang o che do "chay toan luong"; con lai la id cua buoc dang chon.
  const [chon, setChon] = useState<string | null>(null);
  const [full, setFull] = useState<FullRunState | null>(null);
  const [giaTri, setGiaTri] = useState<ParamValues>({});
  const [runId, setRunId] = useState<string | null>(null);
  const [loi, setLoi] = useState<unknown>(null);
  const [dangGui, setDangGui] = useState(false);
  const [confirm, setConfirm] = useState("");

  const { run, log, noi, dangChay } = useRunStream(runId);
  const step = steps.find((s) => s.id === chon) ?? null;

  // Nap mo ta buoc + bat lai lan chay dang do (vd vua F5 giua chung).
  useEffect(() => {
    api
      .steps()
      .then((r) => {
        setSteps(r.steps);
        setConfirmPhrase(r.confirm_phrase);
        setFull(khoiTaoFull(r.steps, r.chuoi_mac_dinh));
        if (r.steps.length) {
          setChon((cu) => cu ?? r.steps[0].id);
          setGiaTri((cu) => (Object.keys(cu).length ? cu : macDinh(r.steps[0])));
        }
      })
      .catch(setLoi);

    api.runs(1).then((r) => {
      if (r.dang_chay) setRunId(r.dang_chay.id);
    }).catch(() => undefined);
  }, []);

  // Chay xong thi so lieu o thanh trang thai co the da doi.
  useEffect(() => {
    if (run && !dangChay) napLaiHealth();
  }, [run?.status]);

  const doiBuoc = (s: Step) => {
    setChon(s.id);
    setGiaTri(macDinh(s));
    setLoi(null);
    setConfirm("");
  };

  const laFull = chon === null;
  const canConfirm = laFull
    ? Boolean(full && coNguyHiem(steps, full))
    : Boolean(step?.params.some((p) => p.danger && giaTri[p.name]));

  // Nut Chay bi khoa khi: dang chay, thieu script, thieu o bat buoc, hoac
  // co buoc ghi that ma chua go dung cau xac nhan.
  const thieuOFull = laFull && full ? thieuThamSo(steps, full) : [];
  const soBuocFull = laFull && full ? steps.filter((x) => full.chon[x.id]).length : 0;
  const khoaChay =
    dangGui ||
    dangChay ||
    (canConfirm && confirm.trim() !== confirmPhrase) ||
    (laFull
      ? soBuocFull === 0 || thieuOFull.length > 0
      : !step || !step.script_exists);

  const chay = async () => {
    setLoi(null);
    setDangGui(true);
    try {
      let r;
      if (laFull && full) {
        const ds = steps.filter((s) => full.chon[s.id]).map((s) => s.id);
        const ps: Record<string, ParamValues> = {};
        for (const id of ds) ps[id] = full.params[id] ?? {};
        r = await api.runChuoi(ds, ps, confirm);
      } else {
        if (!step) return;
        r = await api.runStep(step.id, giaTri, confirm);
      }
      setRunId(r.run.id);
    } catch (e) {
      setLoi(e);
      if (e instanceof ApiError && e.status === 409) {
        // Dang ban: bat lay lan chay hien tai de nguoi dung nhin thay no.
        const ds = await api.runs(1).catch(() => null);
        if (ds?.dang_chay) setRunId(ds.dang_chay.id);
      }
    } finally {
      setDangGui(false);
    }
  };

  const dung = async () => {
    if (!runId) return;
    await api.cancelRun(runId).catch(setLoi);
  };

  if (!steps.length) {
    return (
      <div className="content-pad">
        {loi ? <ErrorBox error={loi} /> : <Spinner />}
      </div>
    );
  }

  return (
    <div className="content-pad" style={{ maxWidth: 1400, height: "100%" }}>
      <div className="pipe">
        {/* --- cot trai: chay toan luong + 6 buoc --- */}
        <div className="pipe-steps">
          <div
            className={`step-card ${laFull ? "active" : ""}`}
            onClick={() => {
              setChon(null);
              setLoi(null);
              setConfirm("");
            }}
            style={{ borderStyle: "dashed" }}
          >
            <div className="step-top">
              <span className="step-label grow">▶▶ Chạy toàn bộ luồng</span>
              {run?.step_id === "full" && dangChay ? (
                <span className="dot dot-pulse" style={{ color: "var(--info)" }} />
              ) : null}
            </div>
            <div className="step-sum">
              Nối tiếp các bước trong một lần chạy, hỏng ở đâu dừng ở đó.
            </div>
          </div>
          <div className="step-arrow">━</div>

          {steps.map((s, i) => (
            <div key={s.id}>
              <div
                className={`step-card ${chon === s.id ? "active" : ""}`}
                onClick={() => doiBuoc(s)}
              >
                <div className="step-top">
                  <span className="step-label grow">{s.label}</span>
                  {run?.step_id === s.id && dangChay ? (
                    <span className="dot dot-pulse" style={{ color: "var(--info)" }} />
                  ) : null}
                </div>
                <div className="step-sum">{s.summary}</div>
                <CoBuoc step={s} />
              </div>
              {i < steps.length - 1 ? <div className="step-arrow">↓</div> : null}
            </div>
          ))}
        </div>

        {/* --- cot phai: tham so + log --- */}
        <div className="col" style={{ minHeight: 0, gap: 16 }}>
          {laFull || step ? (
            <div className="card">
              <div className="card-head">
                <h2 className="grow">
                  {laFull ? "Chạy toàn bộ luồng" : step!.label}
                </h2>
                <span className="mono muted">
                  {laFull ? `${soBuocFull} bước` : step!.script}
                </span>
              </div>
              <div className="card-body">
                {laFull && full ? (
                  <FullRunPanel
                    steps={steps}
                    state={full}
                    onChange={setFull}
                    health={health}
                    confirmPhrase={confirmPhrase}
                    confirm={confirm}
                    onConfirm={setConfirm}
                  />
                ) : (
                  <>
                <ThieuDieuKien step={step!} health={health} />

                <ParamForm step={step!} values={giaTri} onChange={setGiaTri} />

                {step!.params.some((p) => p.danger) && (
                  <div className="danger-zone" style={{ marginTop: 16 }}>
                    <div className="dz-title">⚠ Vùng ghi thật — không hoàn tác được</div>
                    <div className="col" style={{ gap: 9 }}>
                      {step!.params
                        .filter((p) => p.danger)
                        .map((p) => (
                          <div key={p.name}>
                            <Switch
                              checked={Boolean(giaTri[p.name])}
                              onChange={(v) => setGiaTri({ ...giaTri, [p.name]: v })}
                              label={p.label}
                            />
                            {p.help ? <div className="hint">{p.help}</div> : null}
                          </div>
                        ))}
                    </div>
                    {canConfirm && (
                      <div className="field" style={{ marginTop: 12 }}>
                        <label className="label">
                          <span className="req">*</span>
                          Gõ <code className="mono">{confirmPhrase}</code> để xác nhận
                        </label>
                        <input
                          className="input"
                          value={confirm}
                          onChange={(e) => setConfirm(e.target.value)}
                          placeholder={confirmPhrase}
                          autoComplete="off"
                        />
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}

                {loi ? <div style={{ marginTop: 14 }}><ErrorBox error={loi} /></div> : null}

                <div className="row" style={{ marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={chay} disabled={khoaChay}>
                    {dangGui ? <Spinner /> : laFull ? "▶▶" : "▶"}
                    {dangChay
                      ? "Đang có bước chạy"
                      : laFull
                        ? `Chạy ${soBuocFull} bước nối tiếp`
                        : "Chạy bước này"}
                  </button>
                  {dangChay ? (
                    <button className="btn btn-danger" onClick={dung}>Dừng</button>
                  ) : null}
                  <span className="grow" />
                  {!laFull && step?.reads ? (
                    <span className="small muted">đọc: {step.reads}</span>
                  ) : null}
                  {!laFull && step?.writes ? (
                    <span className="small muted">→ ghi: {step.writes}</span>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <Console
            log={log}
            run={run}
            dangChay={dangChay}
            onCancel={dung}
            trangThaiNoi={noi}
          />
        </div>
      </div>
    </div>
  );
}
